/**
 * Role duty templates — one page to maintain the standard "what this job
 * does" list for every designation. New hires with a matching designation
 * get the list filled in automatically.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ClipboardList, Loader2, Plus, Save, Trash2, ChevronDown, ChevronRight,
  UsersRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';

const blank = { role: '', role_summary: '', duties_text: '' };

const DutyTemplatesPage = () => {
  const { authHeader } = useAuth();
  const [rows, setRows] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState('');
  const [edits, setEdits] = useState({});
  const [adding, setAdding] = useState(null);
  const [backfilled, setBackfilled] = useState(null);

  const backfill = async () => {
    setBusy('backfill');
    try {
      const { data } = await axios.post(`${API}/skills/duty-templates/backfill`, {},
        { headers: authHeader() });
      setBackfilled(data);
      toast.success(data.updated
        ? `Filled duties for ${data.updated} staff member${data.updated === 1 ? '' : 's'}`
        : 'Everyone already has duties recorded');
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/skills/duty-templates`, { headers: authHeader() });
      setRows(data.templates || []);
      setCanEdit(!!data.can_edit);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (t) => {
    setOpen(t.id);
    setEdits((p) => ({
      ...p,
      [t.id]: {
        role: t.role || '',
        role_summary: t.role_summary || '',
        duties_text: (t.duties || []).join('\n'),
      },
    }));
  };

  const patch = (id, key, value) => setEdits((p) => ({ ...p, [id]: { ...p[id], [key]: value } }));

  const save = async (id) => {
    const e = edits[id] || {};
    setBusy(id);
    try {
      const { data } = await axios.put(`${API}/skills/duty-templates/${id}`, {
        role: e.role,
        role_summary: e.role_summary,
        duties: (e.duties_text || '').split('\n').map((d) => d.trim()).filter(Boolean),
      }, { headers: authHeader() });
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success(`“${data.role}” template saved`);
      setOpen('');
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(''); }
  };

  const create = async () => {
    setBusy('new');
    try {
      const { data } = await axios.post(`${API}/skills/duty-templates`, {
        role: adding.role,
        role_summary: adding.role_summary,
        duties: (adding.duties_text || '').split('\n').map((d) => d.trim()).filter(Boolean),
      }, { headers: authHeader() });
      toast.success(`“${data.role}” template added`);
      setAdding(null);
      await load();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(''); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete the “${t.role}” duty template?`)) return;
    setBusy(t.id);
    try {
      await axios.delete(`${API}/skills/duty-templates/${t.id}`, { headers: authHeader() });
      setRows((prev) => prev.filter((r) => r.id !== t.id));
      toast.success('Template deleted');
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(''); }
  };

  const field = 'w-full rounded-lg border border-slate-300 px-2.5 py-1.5 text-sm focus:border-teal-500 focus:outline-none';

  if (loading) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="space-y-5 pb-20" data-testid="duty-templates-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/skills" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1" data-testid="back-to-matrix">
            <ArrowLeft size={14} /> Skill matrix
          </Link>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ClipboardList size={20} className="text-teal-600" /> Role duty templates
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            The standard job for each designation. A new staff member added with a matching
            designation gets these duties filled in automatically.
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={backfill} disabled={busy === 'backfill'}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              title="Fill the standard duties for every active staff member who has none"
              data-testid="backfill-duties-btn"
            >
              {busy === 'backfill' ? <Loader2 size={15} className="animate-spin" /> : <UsersRound size={15} />}
              Fill existing staff
            </button>
            <button
              onClick={() => setAdding({ ...blank })}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-teal-600 text-sm font-medium text-white hover:bg-teal-700"
              data-testid="add-template-btn"
            >
              <Plus size={15} /> New template
            </button>
          </div>
        )}
      </div>

      {backfilled && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 text-sm" data-testid="backfill-result">
          <p className="text-slate-800">
            <b>{backfilled.updated}</b> staff member{backfilled.updated === 1 ? '' : 's'} filled from templates.
          </p>
          {(backfilled.without_template || []).length > 0 && (
            <p className="text-slate-600 mt-1.5">
              No template matched these designations — add one and run this again:{' '}
              {backfilled.without_template.map((w) => `${w.name} (${w.role || 'no designation'})`).join(', ')}
            </p>
          )}
        </div>
      )}

      {adding && (
        <div className="bg-white border border-teal-200 rounded-xl p-4 space-y-3" data-testid="new-template-form">
          <h2 className="text-sm font-semibold text-slate-800">New role template</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Designation</label>
              <input
                className={`mt-0.5 ${field}`} value={adding.role}
                onChange={(e) => setAdding((p) => ({ ...p, role: e.target.value }))}
                placeholder="e.g. Wrap Specialist" data-testid="new-template-role"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Role summary</label>
              <input
                className={`mt-0.5 ${field}`} value={adding.role_summary}
                onChange={(e) => setAdding((p) => ({ ...p, role_summary: e.target.value }))}
                placeholder="One line describing the job" data-testid="new-template-summary"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500">Duties — one per line</label>
            <textarea
              rows={6} className={`mt-0.5 ${field}`} value={adding.duties_text}
              onChange={(e) => setAdding((p) => ({ ...p, duties_text: e.target.value }))}
              data-testid="new-template-duties"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={create} disabled={busy === 'new'}
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:bg-slate-300"
              data-testid="save-new-template-btn"
            >
              {busy === 'new' ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save template
            </button>
            <button onClick={() => setAdding(null)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="templates-list">
        {rows.map((t) => {
          const isOpen = open === t.id;
          const e = edits[t.id] || {};
          return (
            <div key={t.id} data-testid={`template-row-${t.id}`}>
              <div className="flex items-center gap-3 p-3.5">
                <button
                  onClick={() => (isOpen ? setOpen('') : startEdit(t))}
                  className="flex-1 flex items-center gap-2 text-left"
                  data-testid={`template-toggle-${t.id}`}
                >
                  {isOpen ? <ChevronDown size={15} className="text-slate-400" /> : <ChevronRight size={15} className="text-slate-400" />}
                  <span>
                    <span className="text-sm font-medium text-slate-900">{t.role}</span>
                    <span className="ml-2 text-xs text-slate-500">{(t.duties || []).length} duties</span>
                    {t.role_summary && (
                      <span className="block text-xs text-slate-500 mt-0.5">{t.role_summary}</span>
                    )}
                  </span>
                </button>
                {canEdit && (
                  <button
                    onClick={() => remove(t)} disabled={busy === t.id}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                    title="Delete template" data-testid={`delete-template-${t.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="px-3.5 pb-4 space-y-3">
                  {canEdit ? (
                    <>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <label className="text-[11px] uppercase tracking-wider text-slate-500">Designation</label>
                          <input
                            className={`mt-0.5 ${field}`} value={e.role ?? ''}
                            onChange={(ev) => patch(t.id, 'role', ev.target.value)}
                            data-testid={`edit-role-${t.id}`}
                          />
                        </div>
                        <div>
                          <label className="text-[11px] uppercase tracking-wider text-slate-500">Role summary</label>
                          <input
                            className={`mt-0.5 ${field}`} value={e.role_summary ?? ''}
                            onChange={(ev) => patch(t.id, 'role_summary', ev.target.value)}
                            data-testid={`edit-summary-${t.id}`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[11px] uppercase tracking-wider text-slate-500">Duties — one per line (max 30)</label>
                        <textarea
                          rows={Math.min(16, ((e.duties_text || '').split('\n').length + 2))}
                          className={`mt-0.5 ${field}`} value={e.duties_text ?? ''}
                          onChange={(ev) => patch(t.id, 'duties_text', ev.target.value)}
                          data-testid={`edit-duties-${t.id}`}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => save(t.id)} disabled={busy === t.id}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:bg-slate-300"
                          data-testid={`save-template-${t.id}`}
                        >
                          {busy === t.id ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
                        </button>
                        <button onClick={() => setOpen('')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">
                          Close
                        </button>
                      </div>
                    </>
                  ) : (
                    <ul className="space-y-1">
                      {(t.duties || []).map((d, i) => (
                        <li key={`${i}-${d}`} className="flex gap-2 text-sm text-slate-700">
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-teal-500" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="p-6 text-sm text-slate-500" data-testid="templates-empty">No templates yet.</p>
        )}
      </div>
    </div>
  );
};

export default DutyTemplatesPage;
